import { apiClient } from '../client';
import { contactTypeToApiQueryValue, normalizeContactType, readContactTypeField } from '../../../features/contacts/contactType';
import type {
  CampaignOption,
  ContactOption,
  CustomerSegmentOption,
  GetContactOptionsParams,
  GetCustomerSegmentOptionsParams,
  GetLeadOptionsParams,
  GetOfferingCategoryOptionsParams,
  GetOfferingOptionsParams,
  LeadOption,
  OfferingCategoryOption,
  OfferingOption,
  UserOption,
} from './types';

export type {
  CampaignOption,
  ContactOption,
  CustomerSegmentOption,
  GetContactOptionsParams,
  GetCustomerSegmentOptionsParams,
  GetLeadOptionsParams,
  GetOfferingCategoryOptionsParams,
  GetOfferingOptionsParams,
  LeadOption,
  OfferingCategoryOption,
  OfferingOption,
  UserOption,
} from './types';

function appendOptionalBoolean(params: URLSearchParams, key: string, value: boolean | undefined): void {
  if (value !== undefined) {
    params.set(key, String(value));
  }
}

export async function getContactOptions(params: GetContactOptionsParams = {}): Promise<ContactOption[]> {
  const searchParams = new URLSearchParams();
  const { search, categoryId, contactType, ownerUserId, role } = params;
  if (search !== undefined && search !== '') searchParams.set('search', search);
  if (categoryId !== undefined) searchParams.set('categoryId', String(categoryId));
  if (contactType !== undefined) searchParams.set('contactType', contactTypeToApiQueryValue(contactType));
  if (ownerUserId !== undefined && ownerUserId !== '') searchParams.set('ownerUserId', ownerUserId);
  if (role !== undefined) searchParams.set('role', String(role));

  const query = searchParams.toString();
  const response = await apiClient.get<ContactOption[]>(
    `/metadata/options/contacts${query ? `?${query}` : ''}`,
  );
  return response.data.map((item) => ({
    ...item,
    contactType: normalizeContactType(readContactTypeField(item)),
  }));
}

export async function getLeadOptions(params: GetLeadOptionsParams = {}): Promise<LeadOption[]> {
  const searchParams = new URLSearchParams();
  const { search, contactId, campaignId } = params;
  if (search !== undefined && search !== '') searchParams.set('search', search);
  if (contactId !== undefined && contactId !== '') searchParams.set('contactId', contactId);
  if (campaignId !== undefined && campaignId !== '') searchParams.set('campaignId', campaignId);

  const query = searchParams.toString();
  const response = await apiClient.get<LeadOption[]>(
    `/metadata/options/leads${query ? `?${query}` : ''}`,
  );
  return response.data;
}

export async function getOfferingOptions(params: GetOfferingOptionsParams = {}): Promise<OfferingOption[]> {
  const searchParams = new URLSearchParams();
  appendOptionalBoolean(searchParams, 'activeOnly', params.activeOnly ?? true);

  const query = searchParams.toString();
  const response = await apiClient.get<OfferingOption[]>(
    `/metadata/options/offerings${query ? `?${query}` : ''}`,
  );
  return response.data;
}

export async function getCampaignOptions(): Promise<CampaignOption[]> {
  const response = await apiClient.get<CampaignOption[]>('/metadata/options/campaigns');
  return response.data;
}

export async function getCustomerSegmentOptions(
  params: GetCustomerSegmentOptionsParams = {},
): Promise<CustomerSegmentOption[]> {
  const searchParams = new URLSearchParams();
  appendOptionalBoolean(searchParams, 'activeOnly', params.activeOnly ?? true);

  const query = searchParams.toString();
  const response = await apiClient.get<CustomerSegmentOption[]>(
    `/metadata/options/customer-segments${query ? `?${query}` : ''}`,
  );
  return response.data;
}

export async function getOfferingCategoryOptions(
  params: GetOfferingCategoryOptionsParams = {},
): Promise<OfferingCategoryOption[]> {
  const searchParams = new URLSearchParams();
  appendOptionalBoolean(searchParams, 'activeOnly', params.activeOnly ?? true);

  const query = searchParams.toString();
  const response = await apiClient.get<OfferingCategoryOption[]>(
    `/metadata/options/offering-categories${query ? `?${query}` : ''}`,
  );
  return response.data;
}

export async function getUserOptions(): Promise<UserOption[]> {
  const response = await apiClient.get<UserOption[]>('/metadata/options/users');
  return response.data;
}
